import { useEffect, useRef, useState } from "react";

export default function EmailCodeVerification({ formData, onVerified }) {
    const CODE_LENGTH = 6;
    const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
    const inputsRef = useRef([]);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [nbCode, setNbCode] = useState(1);

    // --------------------------
    // Renvoyer le code
    // --------------------------
    const resendCode = async () => {
        setLoading(true);
        setMessage("");

        try {
            const form = new FormData();
            form.append("email", formData.email);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/register/sendCode`,
                {
                    method: "POST",
                    body: form
                }
            );

            const data = await response.json();
            if (!response.ok) {
                setMessage(data.error || "Impossible d'envoyer le code");
            } else {
                if (nbCode > 1) setMessage("📨 Nouveau code envoyé !");
                setDigits(Array(CODE_LENGTH).fill(""));
                inputsRef.current[0]?.focus();
            }
        } catch {
            setMessage("Erreur serveur");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        resendCode();
    }, []);

    const handleChange = (value, index) => {
        if (!/^[0-9]?$/.test(value)) return;

        const newDigits = [...digits];
        newDigits[index] = value;
        setDigits(newDigits);

        if (value && index < CODE_LENGTH - 1) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
    };

    const verifyCode = async () => {
        const code = digits.join("");

        if (code.length < CODE_LENGTH) {
            setMessage("Code incomplet");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const form = new FormData();
            form.append("email", formData.email);
            form.append("code", code);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/register/verify`,
                {
                    method: "POST",
                    body: form
                }
            );

            const data = await response.json();

            if (!response.ok) {
                setMessage(data.error || "Erreur lors de la vérification");
            } else {
                setMessage("✔ Code vérifié !");
                onVerified(data.data.token);
            }
        } catch {
            setMessage("Erreur serveur");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 20,
                marginTop: 30,
                padding: "0",
                width: "100%",
                overflow:"hidden"
            }}
        >
            <h2 style={{ fontSize: 22, fontWeight: "bold" }}>
                Vérification de l’email
            </h2>

            <p style={{ color: "#666", textAlign: "center" }}>
                Un code à 6 chiffres a été envoyé à :
                <br />
                <strong>{formData.email}</strong>
            </p>

            {/* Cases du code */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "nowrap",   // ⬅ toujours sur une seule ligne
                    width: "100%",
                    maxWidth: "360px",    // ⬅ limite pour éviter le débordement
                    gap: 8
                }}
            >
                {digits.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        value={digit}
                        maxLength={1}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        inputMode="numeric"
                        style={{
                            width: "calc(100% / 6 - 8px)",   // ⬅ garantit 1 seule ligne
                            aspectRatio: "1 / 1.2",
                            fontSize: "clamp(20px, 6vw, 28px)",
                            textAlign: "center",
                            borderRadius: 10,
                            border: "2px solid #ddd",
                            background: "#fafafa",
                            outline: "none",
                            transition: "0.2s",
                        }}
                    />
                ))}
            </div>

            <div style={{ color: "#c00", minHeight: 20 }}>
                {message && <span>{message}</span>}
            </div>

            <button
                onClick={verifyCode}
                disabled={loading}
                style={{
                    background: "#007bff",
                    color: "white",
                    padding: "12px 24px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    width: 220
                }}
            >
                {loading ? "..." : "Valider le code"}
            </button>

            <button
                onClick={() => { setNbCode(2); resendCode() }}
                disabled={loading}
                style={{
                    background: "transparent",
                    color: "#007bff",
                    border: "none",
                    cursor: "pointer",
                    marginTop: 10,
                    fontSize: 15,
                    textDecoration: "underline"
                }}
            >
                Renvoyer le code
            </button>
        </div>
    );
}
